import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Activity, AlertCircle, AlertTriangle, Clock } from "lucide-react";
import {
  fetchDashboardStatus,
  fetchDashboardAlerts,
} from "../../../store/dashboardSlice";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import DashboardChart from "./DashboardChart";

const StatusCard = ({ title, count }) => (
  <div className="bg-white rounded-lg shadow-md p-4 flex justify-between items-center hover:shadow-lg transition-shadow duration-200">
    <span className="font-semibold text-gray-700">{title}</span>
    <span className="text-lg font-bold text-blue-600">{count}</span>
  </div>
);

const AlertButton = ({ title, count, icon: Icon, colorClass, path }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className={`bg-white rounded-lg shadow-md p-4 flex items-center justify-between hover:shadow-lg transition-shadow duration-200 ${colorClass}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" />
        <span className="font-semibold text-gray-700">{title}</span>
      </div>
      <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">
        {count} items
      </span>
    </button>
  );
};

const LoadingOverlay = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4 mx-auto"></div>
      <p className="text-center">Loading dashboard data...</p>
    </div>
  </div>
);

const ErrorMessage = ({ message, onRetry }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
      <div className="text-red-500 mb-4 text-center">
        <AlertCircle className="h-12 w-12 mx-auto mb-2" />
        <h3 className="text-lg font-bold">Error Loading Dashboard</h3>
      </div>
      <p className="text-center mb-4">{message}</p>
      <button
        onClick={onRetry}
        className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition-colors"
      >
        Retry
      </button>
    </div>
  </div>
);

const getFilteredStatus = (status, role) => {
  if (!status || !role) return {};
  
  switch (role) {
    case 'project_engineer':
      return { pe: status.pe };
    case 'location_head':
      return { l1: status.l1 };
    case 'director':
      return { l2: status.l2 };
    case 'purchase_department':
    case 'project_head':
    case 'store_head':
    case 'store_officer':
    case 'admin':
      return status;
    default:
      return {};
  }
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { status, alerts, loading, error } = useSelector((state) => state.dashboard);
  const { role } = useSelector((state) => state.auth);

  const fetchData = () => {
    dispatch(fetchDashboardStatus());
    dispatch(fetchDashboardAlerts());
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const filteredStatus = getFilteredStatus(status, role);

  const alertButtons = [
    {
      title: "Expired",
      items: alerts?.default || [],
      icon: Clock,
      colorClass: "border-l-4 border-gray-500",
      path: "/alerts/expired",
    },
    {
      title: "Due Today",
      items: alerts?.danger || [],
      icon: AlertCircle,
      colorClass: "border-l-4 border-red-500",
      path: "/alerts/due-today",
    },
    {
      title: "Due Soon",
      items: alerts?.alert || [],
      icon: AlertTriangle,
      colorClass: "border-l-4 border-yellow-500",
      path: "/alerts/due-soon",
    },
    {
      title: "Upcoming",
      items: alerts?.warning || [],
      icon: Activity,
      colorClass: "border-l-4 border-orange-500",
      path: "/alerts/upcoming",
    },
  ];

  if (loading) return <LoadingOverlay />;
  if (error) return <ErrorMessage message={error} onRetry={fetchData} />;

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />

      <main className="flex-grow p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Alerts Section */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            {alertButtons.map((button, index) => (
              <AlertButton
                key={index}
                title={button.title}
                count={button.items?.length || 0}
                icon={button.icon}
                colorClass={button.colorClass}
                path={button.path}
              />
            ))}
          </div>

          {/* Chart Section */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <DashboardChart alerts={alerts || {}} />
          </div>

          {/* Status Section */}
          <div className="bg-white rounded-lg shadow-md p-4 lg:p-6">
            <h2 className="text-lg lg:text-xl font-bold mb-4">Status</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(filteredStatus).map(([key, value]) => (
                <StatusCard key={key} title={key} count={value || 0} />
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;